/**
 * Greedy word wrapping with character fallback and `lineLimit` truncation.
 */

import type { ResolvedFont, Size, TextFragment, TextMeasurer } from './types';

export const ELLIPSIS = '…';
/**
 * Slack when comparing a line's width with the proposal. Frames are snapped
 * to whole pixels and custom layouts round to 2 decimals, so a text proposed
 * its own measured width can arrive a fraction narrower; half a pixel never
 * changes a line break visibly but stops single words breaking apart.
 */
const EPSILON = 0.5;

export interface WrappedText {
  lines: string[];
  /** Width of the widest line. */
  width: number;
  truncated: boolean;
}

/**
 * Wrap `text` to `maxWidth` (null or non-finite = a single line per
 * paragraph). Words are split at spaces; a word wider than the line is broken
 * by characters. With `lineLimit`, extra lines are dropped and the last kept
 * line is truncated with an ellipsis so it fits.
 */
export function wrapText(
  text: string,
  font: ResolvedFont,
  maxWidth: number | null,
  lineLimit: number | null,
  measurer: TextMeasurer,
): WrappedText {
  const measure = (s: string): number => (s.length === 0 ? 0 : measurer.measureWidth(s, font));
  const constrained = maxWidth !== null && Number.isFinite(maxWidth);
  const paragraphs = text.split('\n');
  let lines: string[] = [];
  if (constrained) {
    for (const paragraph of paragraphs) wrapParagraph(paragraph, maxWidth, measure, lines);
  } else {
    lines = paragraphs;
  }
  if (lines.length === 0) lines = [''];

  let truncated = false;
  if (lineLimit !== null && lineLimit >= 1 && lines.length > lineLimit) {
    const kept = lines.slice(0, lineLimit - 1);
    const rest = lines
      .slice(lineLimit - 1)
      .map((l) => l.trim())
      .filter((l) => l.length > 0)
      .join(' ');
    kept.push(truncateLine(rest, constrained ? maxWidth : null, measure));
    lines = kept;
    truncated = true;
  }

  let width = 0;
  for (const line of lines) width = Math.max(width, measure(line));
  return { lines, width, truncated };
}

function wrapParagraph(
  paragraph: string,
  maxWidth: number,
  measure: (s: string) => number,
  out: string[],
): void {
  const words = paragraph.split(' ').filter((w) => w.length > 0);
  if (words.length === 0) {
    out.push('');
    return;
  }
  let current = '';
  for (const word of words) {
    const candidate = current.length === 0 ? word : `${current} ${word}`;
    if (measure(candidate) <= maxWidth + EPSILON) {
      current = candidate;
      continue;
    }
    if (current.length > 0) out.push(current);
    if (measure(word) <= maxWidth + EPSILON) {
      current = word;
      continue;
    }
    // The word alone is wider than the line: break it by characters.
    const chunks = breakWord(word, maxWidth, measure);
    for (let i = 0; i < chunks.length - 1; i++) out.push(chunks[i] as string);
    current = chunks[chunks.length - 1] ?? '';
  }
  out.push(current);
}

function breakWord(word: string, maxWidth: number, measure: (s: string) => number): string[] {
  const chunks: string[] = [];
  let chunk = '';
  for (const ch of word) {
    const candidate = chunk + ch;
    if (chunk.length === 0 || measure(candidate) <= maxWidth + EPSILON) {
      chunk = candidate; // at least one character per line
    } else {
      chunks.push(chunk);
      chunk = ch;
    }
  }
  if (chunk.length > 0) chunks.push(chunk);
  return chunks;
}

function truncateLine(
  text: string,
  maxWidth: number | null,
  measure: (s: string) => number,
): string {
  if (maxWidth === null) return text + ELLIPSIS;
  const chars = Array.from(text);
  while (chars.length > 0 && measure(chars.join('') + ELLIPSIS) > maxWidth + EPSILON) {
    chars.pop();
    while (chars.length > 0 && chars[chars.length - 1] === ' ') chars.pop();
  }
  return chars.join('') + ELLIPSIS;
}

// ---------------------------------------------------------------------------
// Rich text: runs
// ---------------------------------------------------------------------------

/** A run of rich text as the wrapper reads it: its text or image, and its font. */
export interface TextRunInput {
  text?: string;
  /** An inline image's size (the run has no text). */
  image?: Size;
  font: ResolvedFont;
  /** Extra space after every character, in px. */
  kerning: number;
}

export interface WrappedRuns {
  lines: TextFragment[][];
  /** Each line's height: its tallest font's line height or image. */
  heights: number[];
  /** Width of the widest line. */
  width: number;
  truncated: boolean;
}

type Piece = TextFragment;
type Token = { kind: 'word'; pieces: Piece[] } | { kind: 'space'; piece: Piece } | { kind: 'break' };

/**
 * `wrapText` for runs of differently styled text and inline images: lines
 * break at spaces (and around images), a word may span runs, a word wider
 * than the line breaks by characters, and `lineLimit` truncates the last
 * kept line with an ellipsis in its last run's font. `base` sets the height
 * of an empty line.
 */
export function wrapRuns(
  runs: TextRunInput[],
  base: ResolvedFont,
  maxWidth: number | null,
  lineLimit: number | null,
  measurer: TextMeasurer,
): WrappedRuns {
  const measure = (run: number, text: string): number => {
    if (text.length === 0) return 0;
    const input = runs[run] as TextRunInput;
    return measurer.measureWidth(text, input.font) + input.kerning * Array.from(text).length;
  };
  const constrained = maxWidth !== null && Number.isFinite(maxWidth);
  const limit = constrained ? maxWidth + EPSILON : Infinity;

  // Tokens: words (which may span runs), spaces and hard breaks.
  const tokens: Token[] = [];
  let word: Piece[] | null = null;
  runs.forEach((input, run) => {
    if (input.image) {
      word = null;
      tokens.push({ kind: 'word', pieces: [{ run, width: input.image.width, height: input.image.height }] });
      return;
    }
    for (const part of (input.text ?? '').split(/(\n| +)/)) {
      if (part.length === 0) continue;
      if (part === '\n') {
        word = null;
        tokens.push({ kind: 'break' });
      } else if (part.startsWith(' ')) {
        word = null;
        tokens.push({ kind: 'space', piece: { run, text: part, width: measure(run, part) } });
      } else {
        const piece = { run, text: part, width: measure(run, part) };
        if (word) word.push(piece);
        else {
          word = [piece];
          tokens.push({ kind: 'word', pieces: word });
        }
      }
    }
  });

  let lines: Piece[][] = [];
  let current: Piece[] = [];
  let width = 0;
  let spaces: Piece[] = [];
  const sum = (pieces: Piece[]): number => pieces.reduce((total, piece) => total + piece.width, 0);
  const endLine = (): void => {
    lines.push(current);
    current = [];
    width = 0;
    spaces = [];
  };
  for (const token of tokens) {
    if (token.kind === 'break') {
      endLine();
      continue;
    }
    if (token.kind === 'space') {
      if (current.length > 0) spaces.push(token.piece);
      continue;
    }
    const wordWidth = sum(token.pieces);
    const spaceWidth = sum(spaces);
    if (current.length === 0 ? wordWidth <= limit : width + spaceWidth + wordWidth <= limit) {
      current.push(...spaces, ...token.pieces);
      width += spaceWidth + wordWidth;
      spaces = [];
      continue;
    }
    if (current.length > 0) endLine();
    spaces = [];
    if (wordWidth <= limit) {
      current = [...token.pieces];
      width = wordWidth;
      continue;
    }
    // The word alone is wider than the line: break it by characters.
    for (const piece of token.pieces) {
      const units = piece.text === undefined ? [piece] : Array.from(piece.text).map((ch) => ({ run: piece.run, text: ch, width: measure(piece.run, ch) }));
      for (const unit of units) {
        if (current.length > 0 && width + unit.width > limit) endLine();
        current.push(unit);
        width += unit.width;
      }
    }
  }
  lines.push(current);

  let truncated = false;
  if (lineLimit !== null && lineLimit >= 1 && lines.length > lineLimit) {
    const kept = lines.slice(0, lineLimit - 1);
    const rest: Piece[] = [];
    for (const line of lines.slice(lineLimit - 1)) {
      if (line.length === 0) continue;
      const first = line[0] as Piece;
      if (rest.length > 0) rest.push({ run: first.run, text: ' ', width: measure(first.run, ' ') });
      rest.push(...line);
    }
    kept.push(truncatePieces(rest, constrained ? maxWidth : null, measure, runs.length));
    lines = kept;
    truncated = true;
  }

  const merged = lines.map(mergePieces);
  // The first line is a line tall, each further one a leading (the
  // distance from the line above's baseline).
  const heights = merged.map((line, index) => {
    const metric = (font: ResolvedFont): number => (index === 0 ? font.lineHeight : font.leading);
    if (line.length === 0) return metric(base);
    let height = 0;
    for (const piece of line) {
      const font = (runs[piece.run] as TextRunInput).font;
      height = Math.max(height, piece.height ?? metric(font), metric(font));
    }
    return height;
  });
  let widest = 0;
  for (const line of merged) widest = Math.max(widest, sum(line));
  return { lines: merged, heights, width: widest, truncated };
}

/** The pieces cut to fit `maxWidth` with an ellipsis (in the last run's font) after them. */
function truncatePieces(
  pieces: Piece[],
  maxWidth: number | null,
  measure: (run: number, text: string) => number,
  runCount: number,
): Piece[] {
  const out = pieces.map((piece) => ({ ...piece }));
  const lastRun = (): number => (out.length > 0 ? (out[out.length - 1] as Piece).run : Math.max(0, runCount - 1));
  const ellipsis = (): number => measure(lastRun(), ELLIPSIS);
  const total = (): number => out.reduce((sum, piece) => sum + piece.width, 0);
  const trimEnd = (): void => {
    for (;;) {
      const last = out[out.length - 1];
      if (!last) return;
      if (last.text !== undefined && last.text.length === 0) {
        out.pop();
        continue;
      }
      if (last.text === undefined || !last.text.endsWith(' ')) return;
      last.text = last.text.replace(/ +$/, '');
      last.width = measure(last.run, last.text);
    }
  };
  if (maxWidth !== null) {
    while (out.length > 0 && total() + ellipsis() > maxWidth + EPSILON) {
      const last = out[out.length - 1] as Piece;
      if (last.text === undefined || last.text.length <= 1) out.pop();
      else {
        const chars = Array.from(last.text);
        chars.pop();
        last.text = chars.join('');
        last.width = measure(last.run, last.text);
      }
      trimEnd();
    }
  }
  const run = lastRun();
  const last = out[out.length - 1];
  if (last && last.text !== undefined) {
    last.text += ELLIPSIS;
    last.width = measure(last.run, last.text);
  } else {
    out.push({ run, text: ELLIPSIS, width: measure(run, ELLIPSIS) });
  }
  return out;
}

/** Adjacent text pieces of the same run as one fragment. */
function mergePieces(line: Piece[]): Piece[] {
  const out: Piece[] = [];
  for (const piece of line) {
    const last = out[out.length - 1];
    if (last && last.run === piece.run && last.text !== undefined && piece.text !== undefined) {
      last.text += piece.text;
      last.width += piece.width;
    } else {
      out.push({ ...piece });
    }
  }
  return out;
}
